'use client';

import React, { useContext, useEffect } from 'react';
import LazyLoad from 'react-lazyload';
import camelcaseKeys from 'camelcase-keys';
import { ErrorBoundary, Layout, LayoutBox } from '@benzinga/core-ui';
import { NodeLayout } from '@benzinga/content-manager';
import { disableOptinMonsterCampaigns, raptiveAdManager } from '@benzinga/ads-utils';
import { NoFirstRender } from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { MoneyBlocks } from '@benzinga/money';
import { useBenzingaEdge } from '@benzinga/edge';
import { isGlobalImpressionStored, storeGlobalImpression } from '@benzinga/content-manager';

import {
  NewArticleLayoutWrapper,
  ArticleLayoutMainProps,
  NewArticleLayoutMain,
  ArticleBlocks,
  ArticlePageContext,
  useCampaign,
  useArticlePaywallStatus,
  NewArticleLayoutHeader,
  ArticleInfiniteScrollStories,
  GetBelowArticlePartnerAdBlock,
  WHITELISTED_AUTHORS,
} from '@benzinga/article';
import { getCanonicalUrl, getAuthorName } from '@benzinga/article-manager';
import { isSponsoredArticle } from '@benzinga/article-manager/sponsored-utils';

import { TrackingManager } from '@benzinga/tracking-manager';

const ArticleLayoutSidebar = React.lazy(() =>
  import('./ArticleLayoutSidebar').then(module => {
    return { default: module.ArticleLayoutSidebar };
  }),
);

const Simlink = React.lazy(() =>
  import('@benzinga/ads').then(module => {
    return { default: module.Simlink };
  }),
);

const FloatingWNSTNWidget = React.lazy(() =>
  import('@benzinga/ads').then(module => ({ default: module.FloatingWNSTNWidget })),
);

export const ArticleLayout = ({
  articleData,
  articleIndex,
  articleScrollViewMoreLink,
  baseUrl,
  campaigns,
  campaignSettings,
  campaignStrategy: outerCampaignStrategy,
  deviceType,
  disablePartnerAdOnScroll,
  disablePaywall,
  disableWNSTNWidget,
  executePageviewEvent,
  executeReplaceHistory,
  featuredTickers,
  followUpQuestions,
  googleNewsUrlKey,
  hasAdLight,
  hideTopPanel,
  isBenzingaContributor,
  isBot,
  isDraft,
  isEditor,
  isTaggedPressRelease,
  isTemplate,
  layout,
  loadInfiniteArticles,
  loadMoreButtonVariant,
  partialView,
  postedInVariant,
  primis,
  raptiveEnabled,
  relatedArticles,
  shouldRenderBottomTaboola,
  shouldRenderLeadGenWidget,
  showApiText,
  showFontAwesomeIcons,
  showFullProHeadline,
  showPartnerAd,
  showWhatsAppIcon,
  taboolaSettings,
  trackMeta = true,
  wordCount,
}: ArticleLayoutMainProps) => {
  const articleMeta = camelcaseKeys(articleData?.meta, { deep: true });

  const raptiveAdBlocks = articleData?.blocks?.filter(block => block.blockName === 'acf/raptive-ad-placement') || [];
  const expectedPlaceholderCount = deviceType === 'mobile' ? raptiveAdBlocks.length : raptiveAdBlocks.length + 2;
  raptiveAdManager.setExpectedPlaceholderCount(expectedPlaceholderCount);

  const generatedByAi = articleData?.meta?.Flags?.GeneratedByAI || false;
  const showAdvertiserDisclosure = articleMeta?.flags?.showAdvertiserDisclosure || false;
  const hideCampaign = articleData?.type === 'pr_chainwire';

  const session = useContext(SessionContext);

  const { campaign, campaignStrategy, campaignTicker } = useCampaign(
    articleData,
    outerCampaignStrategy,
    wordCount || 0,
  );

  const authorName = getAuthorName(articleData);
  const shouldDisablePaywall =
    disablePaywall ||
    isBot ||
    showAdvertiserDisclosure ||
    WHITELISTED_AUTHORS.includes(authorName) ||
    isSponsoredArticle(articleData) ||
    isTaggedPressRelease;

  const { headerRef, isNotPaywalled, isPaywallActive, paywall, paywallStyle, setIsPaywallActive } =
    useArticlePaywallStatus(articleData, shouldDisablePaywall, deviceType);

  // Disable OptinMonster popups if campaignStrategy none or if sponsored article.
  React.useEffect(() => {
    if (
      campaignStrategy === 'none' ||
      (campaignStrategy === 'custom' && layout?.popup) ||
      isSponsoredArticle(articleData)
    ) {
      disableOptinMonsterCampaigns();
    }
  }, [campaignStrategy, layout?.popup, articleData]);

  React.useEffect(() => {
    const disableEdgePaywall = () => {
      paywall.disable();
      setIsPaywallActive(false);
    };
    document.addEventListener('om.Campaign.show', disableEdgePaywall);
    return () => {
      document.removeEventListener('om.Campaign.show', disableEdgePaywall);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading: isEdgeLoading } = useBenzingaEdge();

  const trackBottomLeadgenAd = React.useCallback(() => {
    const wid = layout?.article_bottom_widget?.id;
    if (!isGlobalImpressionStored(`leadgen-bottom-article-${wid}`)) {
      storeGlobalImpression(`leadgen-bottom-article-${wid}`);
      session.getManager(TrackingManager).trackCampaignEvent('view', {
        label: layout?.article_bottom_widget?.title ?? '',
        partner_id: `Widget Id: - ${wid}`,
        unit_type: 'leadgen-bottom-article',
      });
    }
  }, [layout, session]);

  useEffect(() => {
    if (isEdgeLoading || !shouldRenderLeadGenWidget || !layout?.article_bottom_widget?.blocks?.length) {
      return;
    }
    trackBottomLeadgenAd();
  }, [isEdgeLoading, shouldRenderLeadGenWidget, layout?.article_bottom_widget?.blocks, trackBottomLeadgenAd]);

  const articleLayoutMainProps = React.useMemo(() => {
    return {
      articleData,
      articleIndex,
      baseUrl,
      campaignSettings,
      campaignStrategy,
      campaignTicker,
      campaigns,
      deviceType,
      disablePaywall,
      disableWNSTNWidget,
      executePageviewEvent,
      executeReplaceHistory,
      featuredTickers,
      followUpQuestions,
      generatedByAi,
      googleNewsUrlKey,
      hasAdLight,
      hideTopPanel,
      isBenzingaContributor,
      isBot,
      isDraft,
      isEditor,
      isPaywallActive,
      isTemplate,
      layout,
      partialView,
      paywall,
      paywallStyle,
      postedInVariant,
      primis,
      raptiveEnabled,
      relatedArticles,
      setIsPaywallActive,
      showAdvertiserDisclosure,
      showApiText,
      showFontAwesomeIcons,
      showPartnerAd,
      showWhatsAppIcon,
      taboolaSettings,
      trackMeta,
      wordCount,
    };
  }, [
    articleData,
    articleIndex,
    baseUrl,
    campaignSettings,
    campaignStrategy,
    campaignTicker,
    campaigns,
    deviceType,
    disablePaywall,
    disableWNSTNWidget,
    executePageviewEvent,
    executeReplaceHistory,
    featuredTickers,
    followUpQuestions,
    generatedByAi,
    googleNewsUrlKey,
    hasAdLight,
    hideTopPanel,
    isBenzingaContributor,
    isBot,
    isDraft,
    isEditor,
    isPaywallActive,
    isTemplate,
    layout,
    partialView,
    paywall,
    paywallStyle,
    postedInVariant,
    primis,
    raptiveEnabled,
    relatedArticles,
    setIsPaywallActive,
    showAdvertiserDisclosure,
    showApiText,
    showFontAwesomeIcons,
    showPartnerAd,
    showWhatsAppIcon,
    taboolaSettings,
    trackMeta,
    wordCount,
  ]);

  const articleUrl = getCanonicalUrl(articleData);

  return (
    <ArticlePageContext.Provider value={{ campaign, campaignStrategy, hide: hideCampaign }}>
      <NewArticleLayoutWrapper className="article-layout-wrapper">
        <Layout
          layoutFooter={
            <ErrorBoundary name="article-layout-footer">
              {layout && <ArticleLayoutFooter layout={layout} />}
            </ErrorBoundary>
          }
          layoutHeader={
            <ErrorBoundary name="article-layout-header">
              <NewArticleLayoutHeader
                articleData={articleData}
                baseUrl={baseUrl}
                campaigns={campaigns}
                deviceType={deviceType}
                featuredTickers={featuredTickers || []}
                googleNewsUrlKey={googleNewsUrlKey}
                headerRef={headerRef}
                hideTopPanel={hideTopPanel}
                isBot={isBot}
                isDraft={isDraft}
                isEditor={isEditor}
                isTemplate={isTemplate}
                layout={layout}
                paywall={paywall}
                showAdvertiserDisclosure={showAdvertiserDisclosure}
                showFullProHeadline={showFullProHeadline}
              />
            </ErrorBoundary>
          }
          layoutHeaderOptions={{ full_width: true }}
          layoutMain={
            <ErrorBoundary name="article-layout-main">
              <NewArticleLayoutMain {...articleLayoutMainProps} />
              {showPartnerAd &&
                !isEdgeLoading &&
                (!shouldRenderLeadGenWidget ? (
                  shouldRenderBottomTaboola && (
                    <ErrorBoundary name="article-layout-main-below-article-partner-ad-block">
                      <React.Suspense fallback={<div />}>
                        <GetBelowArticlePartnerAdBlock
                          id={articleData.nodeId ?? ''}
                          isPaywallActive={isPaywallActive}
                          taboolaSettings={taboolaSettings}
                          url={articleUrl}
                        />
                      </React.Suspense>
                    </ErrorBoundary>
                  )
                ) : (
                  <ErrorBoundary name="article-layout-main-below-article-leadgen-ad-block">
                    <React.Suspense fallback={<div />}>
                      {layout?.article_bottom_widget?.blocks && (
                        <div className="mb-10">
                          <MoneyBlocks blocks={layout?.article_bottom_widget?.blocks} type={'article-bottom'} />
                        </div>
                      )}
                    </React.Suspense>
                  </ErrorBoundary>
                ))}
              {!isBot && loadInfiniteArticles && (
                <React.Suspense fallback={<div />}>
                  <LazyLoad offset={200} once>
                    <ArticleInfiniteScrollStories
                      articleData={articleData}
                      articleScrollViewMoreLink={articleScrollViewMoreLink}
                      deviceType={deviceType}
                      googleNewsUrlKey={googleNewsUrlKey}
                      loadMoreButtonVariant={loadMoreButtonVariant}
                      showFontAwesomeIcons={showFontAwesomeIcons}
                      showPartnerAd={!disablePartnerAdOnScroll}
                      showWhatsAppIcon={showWhatsAppIcon}
                      taboolaSettings={taboolaSettings}
                    />
                  </LazyLoad>
                </React.Suspense>
              )}
            </ErrorBoundary>
          }
          layoutMainClassName="w-full overflow-hidden"
          layoutSidebar={
            isTemplate ? (
              <div className="article-layout-sidebar"></div>
            ) : (
              <ErrorBoundary name="article-layout-sidebar">
                <React.Suspense fallback={<div />}>
                  <ArticleLayoutSidebar
                    article={articleData}
                    campaignStrategy={campaignStrategy}
                    followUpQuestions={followUpQuestions}
                    hasAdLight={hasAdLight}
                    isHeadline={articleData?.isHeadline}
                    layout={layout}
                    nodeId={articleData?.nodeId ?? 0}
                    raptiveEnabled={raptiveEnabled}
                  />
                </React.Suspense>
              </ErrorBoundary>
            )
          }
          sidebarSettings={{ offsetBottom: 120 }}
          title={undefined}
        />
        <React.Suspense fallback={<div />}>{isTaggedPressRelease && <Simlink />}</React.Suspense>
        <React.Suspense fallback={<div />}>
          <ErrorBoundary name="article-layout-floating-wnstn-widget">
            <NoFirstRender>
              <FloatingWNSTNWidget articleID={articleData.nodeId} questions={followUpQuestions} />
            </NoFirstRender>
          </ErrorBoundary>
        </React.Suspense>
      </NewArticleLayoutWrapper>
    </ArticlePageContext.Provider>
  );
};

const ArticleLayoutFooter: React.FC<{ layout: NodeLayout }> = ({ layout }) => {
  return layout?.footer?.blocks ? (
    <LayoutBox>
      <ArticleBlocks blocks={layout.footer.blocks} />
    </LayoutBox>
  ) : null;
};
